import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import MaskedInput from 'react-input-mask';
import Icon from 'Components/Icon';
import { classnames } from 'Utils/classnames';

class Input extends PureComponent {
  static propTypes = {
    id: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.number.isRequired, PropTypes.string.isRequired]),
    name: PropTypes.string.isRequired,
    type: PropTypes.string,
    maxLength: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]),
    mask: PropTypes.any, // Regex.
    maskChar: PropTypes.any,
    readOnly: PropTypes.bool,
    inputMode:
      PropTypes.string /** HTML5 inputMode attribute for mobile keyboard features */,
    pattern:
      PropTypes.string /** HTML5 pattern attribute for mobile keyboard features */,
    disabled: PropTypes.bool,
    validateOnBlur: PropTypes.bool,
    errorMessage: PropTypes.string,
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    onFocus: PropTypes.func,
    onKeyDown: PropTypes.func,
    clearInput: PropTypes.func,
    autoComplete: PropTypes.string,
    autoFocus: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.bool
    ]),
    append: PropTypes.object,
    noAnimationPlaceholder: PropTypes.bool
  };

  static defaultProps = {
    type: 'text'
  };

  constructor (props) {
    super(props);

    this.state = {
      status: '',
      isHidden: props.type === 'password'
    };
  }

  componentWillUnmount () {
    clearTimeout(this.blurTimeout);
  }

  componentDidMount () {
    if (this.props.autoFocus) {
      this.refInput.focus();
    }
  }

  componentDidUpdate (prevProps) {
    if (prevProps.value !== this.props.value && this.props.value === '' && this.state.status !== 'normal') {
      this.setState({ status: 'normal' });
    }
  }

  onChange = event => {
    this.props.onChange(event);
  };

  onBlur = event => {
    const { validateOnBlur } = this.props;

    if (event.target.value.length === 0) {
      this.setState({
        status: 'normal'
      });
    }

    if (this.props.onBlur && validateOnBlur) {
      this.props.onBlur(event);
    }
  };

 onFocus = event => {
   this.setState({ status: 'active' });

   if (this.props.onFocus) {
     this.props.onFocus(event);
   }
 };

  clearInput = id => {
    this.props.clearInput(id);
    this.refInput.focus();

    /** START: Cursor movement for masket input */
    if (this.props.mask) {
      let range = id === 'phone' ? 2 : 0;

      setTimeout(() => {
        this.refInput.setSelectionRange(range, range);
      }, 10);
    }
    /** END: Cursor movement for masket input */
  };

  onKeyDown = event => {
    if (event.key === 'Backspace' && this.props.mask) {
      const currentValue = event.target.value;
      const cursorPosition = event.target.selectionStart;

      if (cursorPosition > 0 && currentValue[cursorPosition - 1] === '/') {
        event.preventDefault();

        const newValue = currentValue.slice(0, cursorPosition - 2) + currentValue.slice(cursorPosition);

        const syntheticEvent = {
          target: {
            id: this.props.id,
            value: newValue
          }
        };

        this.props.onChange(syntheticEvent);

        setTimeout(() => {
          if (this.refInput) {
            this.refInput.setSelectionRange(cursorPosition - 2, cursorPosition - 2);
          }
        }, 0);

        return;
      }
    }

    if (this.props.onKeyDown) {
      this.props.onKeyDown(event);
    }
  };

  togglePassword = () => {
    this.setState(prevState => ({
      isHidden: !prevState.isHidden
    }));
  };

  render () {
    const { status, isHidden } = this.state;
    const {
      id,
      type,
      name,
      value,
      maxLength,
      mask,
      maskChar,
      pattern,
      inputMode,
      errorMessage,
      disabled,
      readOnly,
      autoComplete,
      noAnimationPlaceholder
    } = this.props;
    const labelText = errorMessage || name;
    const inputStatus = errorMessage || value || status === 'active';

    const componentClass = classnames({
      input: true,
      'input--active': !noAnimationPlaceholder && inputStatus,
      'input--error': errorMessage,
      'input--disabled': disabled,
      'input--readonly': readOnly,
      'input--hidden': inputStatus && noAnimationPlaceholder,
    });

    const btnTogglePasswordClass = classnames({
      'input__toggle-password': true,
      'input__toggle-password--show': isHidden,
      'input__toggle-password--active': value !== ''
    });

    const btnClearInputClass = classnames({
      input__clear: true,
      'input__clear--active': value !== ''
    });

    const appendClass = classnames({
      input__append: true,
      'input__append--with-other': type === 'password' || !!this.props.clearInput,
    });

    const inputComponent = mask ? (
      <MaskedInput
        type={ type }
        name={ id }
        id={ id }
        inputRef={ o => (this.refInput = o) }
        placeholder={ name }
        pattern={ pattern }
        inputMode={ inputMode }
        className="input__input"
        mask={ mask }
        maskChar={ maskChar }
        value={ value }
        maxLength={ maxLength }
        disabled={ disabled }
        readOnly={ readOnly }
        onChange={ this.onChange }
        onFocus={ this.onFocus }
        onBlur={ this.onBlur }
        onKeyDown={ this.onKeyDown }
        autoComplete={ autoComplete ? autoComplete : null }
      />
    ) : (
      <input
        type={ type === 'password' && isHidden === false ? 'text' : type }
        name={ id }
        id={ id }
        ref={ o => (this.refInput = o) }
        placeholder={ name }
        pattern={ pattern }
        inputMode={ inputMode }
        className="input__input"
        value={ value }
        maxLength={ maxLength }
        disabled={ disabled }
        readOnly={ readOnly }
        onChange={ this.onChange }
        onFocus={ this.onFocus }
        onBlur={ this.onBlur }
        autoComplete={  autoComplete ? autoComplete : null }
      />
    );

    return (
      <div className={ componentClass }>
        <label className="input__label">{ labelText }</label>

        { inputComponent }

        { type === 'password' && (
          <Icon
            iconName={ isHidden ? 'svgPassHide' : 'svgPassShow' }
            className={ btnTogglePasswordClass }
            onClick={ this.togglePassword }
          />
        ) }

        { this.props.append && (
          <div className={ appendClass }>
            { this.props.append }
          </div>
        ) }

        { this.props.clearInput && (
          <Icon
            iconName="svgClose"
            className={ btnClearInputClass }
            onClick={ () => this.clearInput(id) }
          />
        ) }
      </div>
    );
  }
}

export default Input;
